import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  PauseProctorReasonCodes,
  SessionScope,
  StorageKeyNames,
  firebaseCollections
} from "globals/enums";
import { getDatabase, onValue, ref } from "firebase/database";
import { pauseProctor, resumeProctor } from "../helpers/pauseProctor";
import { useLocation, useNavigate } from "react-router";
import { ProctoringRoute, getEndpoint } from "../routes/route";
import { AlertLogConfig } from "services/session-token";
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import { participantGroupSessionPath } from "helpers/firebase-path";

const useWatchPauseStatus = () => {
  const session = useAppSelector((state) => state.app.session);
  const participantGroupId = useAppSelector((state) => state.app.channelData.participant_group_id);
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const navigate = useNavigate();
  // Re-subscribe on entering/leaving RecorderScreen so a pause set before reaching it (e.g. rejoin + prechecks)
  // is re-delivered. The listener stays in Shell, not RecorderScreen, so it survives the pause screen and can receive resume.
  const { pathname } = useLocation();
  const isOnRecorder = pathname === getEndpoint(ProctoringRoute.RecorderScreen);
  const dispatch = useAppDispatch();

  // Start at false: an initial "not paused" value is not a resume (avoids a spurious Resumed alert and undoing host pause()).
  const lastPauseState = useRef<boolean>(false);

  useEffect(() => {
    if (!session.session_id) return;
    if (
      session.session_type === SessionScope.LiveProctor &&
      !(typeof participantGroupId === "number" && participantGroupId > 0)
    ) {
      return;
    }

    const pauseRef = ref(
      getDatabase(),
      session.session_type === SessionScope.LiveProctor
        ? participantGroupSessionPath(participantGroupId, session.session_id, "pause")
        : `${firebaseCollections.SessionV2}/${session.session_id}/pause`
    );
    const unsubscribe = onValue(pauseRef, (sessionSnapShot) => {
      const isPause = sessionSnapShot.val() || false;
      const currentScreen = sessionStorage.getItem(StorageKeyNames.Screen);

      if (currentScreen !== ProctoringRoute.RecorderScreen) return;

      if (isPause !== lastPauseState.current) {
        lastPauseState.current = isPause;
        if (isPause) {
          alertHelper(alertConfig).raiseAlert(AlertName.ProctoringSessionPaused, dispatch);
          pauseProctor(dispatch, navigate, PauseProctorReasonCodes.PausedByProctor);
        } else {
          resumeProctor(dispatch);
          alertHelper(alertConfig).raiseAlert(AlertName.ProctoringSessionResumed, dispatch);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [
    session.session_id,
    session.session_type,
    participantGroupId,
    alertConfig,
    navigate,
    dispatch,
    isOnRecorder
  ]);

  return null;
};

export default useWatchPauseStatus;
